import React, { useState } from 'react';

import { Box } from '@/components/ui';
import { Iconify } from '@/components/iconify';

type Props = {
  categories: { id: string; name: string }[];
  activeCategory: number;
  setActiveCategory: (category: number) => void;
};

export const BundlesCategories = ({ categories, setActiveCategory, activeCategory }: Props) => {
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  if (categories.length <= 1) return null;

  return (
    <Box className="w-full py-6 md:p-0 md:border-y border-black/20 md:mt-10">
      <Box className="md:hidden relative">
        <button
          type="button"
          onClick={() => setCategoriesOpen((v) => !v)}
          className="w-full cursor-pointer flex items-center justify-between py-3 md:px-4 border-y border-black/20 text-primary-dark font-semibold"
          aria-haspopup="listbox"
          aria-expanded={categoriesOpen}
        >
          <span>{categories[activeCategory]?.name ?? ''}</span>
          <Iconify
            icon={categoriesOpen ? 'lucide:chevron-up' : 'lucide:chevron-down'}
            className="text-gray-700 transition-transform duration-300"
            width={24}
          />
        </button>

        {categoriesOpen && (
          <Box role="listbox" className="absolute left-0 right-0 z-20">
            {/* Standard dropdown inner styles */}
            <Box className="p-1 max-h-[250px] overflow-y-scroll border border-black/10 shadow-[var(--shadow-box)] bg-white/90 backdrop-blur-xl">
              {categories.map((category, idx) => {
                const selected = activeCategory === idx;
                return (
                  <button
                    key={idx}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      setActiveCategory(idx);
                      setCategoriesOpen(false);
                    }}
                    className={`w-full cursor-pointer text-left px-3 py-2 rounded-lg transition-colors ${
                      selected ? 'bg-black/5 text-primary' : 'text-black hover:bg-black/5'
                    }`}
                  >
                    {category.name}
                  </button>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      <Box className="hidden md:flex justify-between xl:flex-nowrap gap-6">
        {categories?.map((category, idx) => {
          const isActive = activeCategory === idx;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveCategory(idx)}
              className={`text-sm cursor-pointer md:text-md md:text-[18px] font-bold py-6 border-b-2 transition-colors ${
                isActive
                  ? 'border-primary text-black'
                  : 'border-transparent text-black hover:border-black/30'
              }`}
            >
              {category.name}
            </button>
          );
        })}
      </Box>
    </Box>
  );
};
